import React, { useState, useRef } from "react";
import { useLazyQuery, useMutation } from "@apollo/client";
import { useHistory } from "react-router-dom";
import {
  Box,
  Flex,
  Spinner,
  Text,
  useToast,
  useDisclosure,
  ModalBody,
  ModalFooter,
} from "@chakra-ui/react";
import { ASN } from "../../graphql/queries";
import { GENERATE_DYNO } from "../../graphql/mutations";
import { Button } from "../../components/Button";
import { InputNumber } from "../../components/InputNumber";
import { ModalBox } from "../../components/ModalBox";
import { SelectionBox } from "../../components/SelectionBox";
import { SelectionBox2 } from "../../components/SelectionBox2";

export const CreateDynoPage = () => {
  const [grades] = useState([
    "1",
    "2",
    "3",
    "4",
    "5",
    "6",
    "7",
    "8",
    "9",
    "10",
    "11",
    "12",
  ]);
  const [standards] = useState(["Common Core", "NGSS"]);
  const [subjects, setSubjects] = useState(["Math", "Science", "English"]);
  const [grade, setGrade] = useState("1");
  const [standard, setStandard] = useState("Common Core");
  const [subject, setSubject] = useState("Math");
  const [domains, setDomains] = useState([]);
  const [domain, setDomain] = useState("");
  const [aSNs, setASNs] = useState([]);
  const [aSN, setASN] = useState({});
  const [topics, setTopics] = useState([]);
  const [topic, setTopic] = useState("");
  const [topicId, setTopicId] = useState("");
  const [subTopics, setSubTopics] = useState([]);
  const [subTopic, setSubTopic] = useState("");
  const [subTopicId, setSubTopicId] = useState("");
  const [subtopicDesc, setSubTopicDesc] = useState("");
  const [fillInTheBlanks, setFillInTheBlanks] = useState(1);
  const [multipleChoice, setMultipleChoice] = useState(1);
  const [searching, setSearching] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [error, setError] = useState(false);
  const idRef = useRef("");

  const { isOpen, onOpen, onClose } = useDisclosure();

  const toast = useToast();
  const history = useHistory();

  const [getASN] = useLazyQuery(ASN, {
    onCompleted: async (data) => {
      setASNs(data.getASN.filter((a) => a.topic.length !== 0));
      setDomains(
        data.getASN.filter((a) => a.topic.length !== 0).map((a) => a.domain)
      );
      setSearching(false);
    },
    onError: () => {
      //Do Nothing
      setSearching(false);
    },
  });

  const [generateDyno] = useMutation(GENERATE_DYNO, {
    onCompleted: (data) => {
      setGenerating(false);
      idRef.current = data.createDynoForDynosPlus.id;
      onOpen();
    },
    onError: () => {
      setGenerating(false);
      toast({
        position: "top",
        title: "Failed to create dyno. Please try again",
        status: "error",
        duration: 9000,
        isClosable: true,
      });
    },
  });

  const onSelect = (type, value) => {
    console.log({ type, value });
    if (type === "grade") {
      setGrade(value);
      setTopics([]);
      setSubTopics([]);
      setDomains([]);
      setDomain("");
      setTopic("");
      setSubTopic("");
    } else if (type === "standard") {
      setStandard(value);
      setSubjects(value === "Common Core" ? ["Math"] : ["Science"]);
      setTopics([]);
      setSubTopics([]);
      setDomains([]);
      setDomain("");
      setTopic("");
      setSubTopic("");
    } else if (type === "subject") {
      setSubject(value);
      setTopics([]);
      setSubTopics([]);
      setDomains([]);
      setDomain("");
      setTopic("");
      setSubTopic("");
    } else if (type === "domain") {
      setDomain(value);
      setASN(aSNs.find((a) => a.domain === value));
      setTopics(aSNs.find((a) => a.domain === value).topic);
      setSubTopics([]);
    } else if (type === "topic") {
      setTopic(value);
      setTopicId(aSN.topic.find((t) => t.statementNotation === value).id);
      setSubTopics(
        aSNs
          .map((a) =>
            a.subTopic.filter((c) => c.statementNotation.includes(value))
          )
          .filter((d) => d.length !== 0)
          .flat()
      );
    } else if (type === "subTopic") {
      const asn = aSN.subTopic.find((s) => s.statementNotation === value);
      setSubTopic(value);
      setSubTopicDesc(asn.description);
      setSubTopicId(asn.id);
    }
  };

  const searchTopic = () => {
    setTopics([]);
    setSubTopics([]);
    setDomain("");
    setTopic("");
    setSubTopic("");
    setSearching(true);
    getASN({
      variables: {
        where: {
          grade: grade,
          subject: subject,
          standard: standard === "NGSS" ? "NGSS (Science)" : "Common Core",
        },
      },
    });
  };

  const setValue = (e, type) => {
    setError(false);
    if (type === "fillInTheBlanks") {
      setFillInTheBlanks(e);
    } else if (type === "multipleChoice") {
      setMultipleChoice(e);
    }
  };

  const generate = () => {
    const totalFields = Number(fillInTheBlanks) + Number(multipleChoice);
    if (totalFields > 10) {
      setError(true);
    } else if (totalFields === 0) {
      setError(true);
    } else {
      setError(false);
      generateDyno({
        variables: {
          where: {
            grade: grade,
            subject: subject,
            standard: standard,
            domain: domain,
            topic: topic,
            topicId: topicId,
            subtopic: subTopic,
            subtopicId: subTopicId,
            subtopicDesc: subtopicDesc,
            isPublicDyno: true,
            questionTypes: {
              MultipleChoice: Number(multipleChoice),
              FillInTheBlanks: Number(fillInTheBlanks),
            },
          },
        },
      });
      setGenerating(true);
    }
  };

  return (
    <Box
      padding="30px 20px"
      position="relative"
      zIndex="1"
      minHeight="calc(100vh - 64px)"
      _after={{
        position: "absolute",
        content: '""',
        top: "0",
        left: "0",
        width: "100%",
        height: "100%",
        backgroundImage: "url(/images/bg.png)",
        backgroundSize: "cover",
        backgroundPosition: "center",
        opacity: "0.4",
        zIndex: "-1",
      }}
    >
      <Box maxWidth="1180px" margin="0 auto">
        <Flex flexDir="column" alignItems="center" justifyContent="center">
          <SelectionBox
            items={grades}
            name="grade"
            padding={true}
            onSelect={onSelect}
            selectedValue={grade}
          />
          <SelectionBox
            items={standards}
            name="standard"
            onSelect={onSelect}
            selectedValue={standard}
          />
          <SelectionBox
            items={subjects}
            name="subject"
            onSelect={onSelect}
            selectedValue={subject}
          />
          <Button
            onClick={searchTopic}
            variant="outline"
            style={{
              borderRadius: "20px",
              minWidth: "130px",
              margin: "0 0 30px",
              borderColor: "rgb(0, 176, 255)",
            }}
            _hover={{ background: "rgb(0, 176, 255)", color: "#fff" }}
          >
            {searching ? <Spinner /> : "Search topic"}
          </Button>
        </Flex>
        <Flex flexDir="column" alignItems="center" justifyContent="center">
          {domains.length > 0 && (
            <SelectionBox
              items={domains}
              name="domain"
              onSelect={onSelect}
              selectedValue={domain}
            />
          )}
          {!!domain && (
            <SelectionBox2
              items={topics}
              name="topic"
              onSelect={onSelect}
              selectedValue={topic}
            />
          )}
          {!!topic && (
            <SelectionBox2
              items={subTopics}
              name="subTopic"
              onSelect={onSelect}
              selectedValue={subTopic}
            />
          )}
          {!!subTopic && (
            <>
              <InputNumber
                defaultValue="1"
                maxW="24"
                max={10}
                label={"No. of fill in the blanks"}
                setValue={setValue}
                value={fillInTheBlanks}
                type="fillInTheBlanks"
              />
              <InputNumber
                defaultValue="1"
                maxW="24"
                max={10}
                label={"No. of multiple choice"}
                setValue={setValue}
                value={multipleChoice}
                type="multipleChoice"
              />
            </>
          )}
          {error && (
            <Text color="tomato" textAlign="center" padding="0 0 10px">
              Total number of questions to generate cannot be 0 and should not
              exceed 10
            </Text>
          )}
          {!!topic && !!subTopic && (
            <Button
              onClick={generate}
              variant="outline"
              style={{
                borderRadius: "20px",
                minWidth: "130px",
                borderColor: "rgb(0, 176, 255)",
              }}
              _hover={{ background: "rgb(0, 176, 255)", color: "#fff" }}
            >
              {generating ? <Spinner /> : "Generate"}
            </Button>
          )}
        </Flex>
        <ModalBox
          title="View or Edit dyno."
          isOpen={isOpen}
          onClose={() => {
            history.push("/");
            onClose();
          }}
        >
          <ModalBody>
            <Text>Dyno has been successfully created.</Text>
          </ModalBody>
          <ModalFooter>
            <Flex justifyContent="center" width="100%">
              <Button
                marginRight="20px"
                onClick={() => {
                  history.push(`/viewer/${idRef.current}`);
                  onClose();
                }}
              >
                View
              </Button>
              <Button
                onClick={() => {
                  history.push(`/editor/${idRef.current}`);
                  onClose();
                }}
              >
                Edit
              </Button>
            </Flex>
          </ModalFooter>
        </ModalBox>
      </Box>
    </Box>
  );
};
